import { format, parseISO } from "date-fns";
import { CalendarOff } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  useIssueDateRangeCalendar,
  type IssueDateField,
} from "@/domains/pm/components/shared/useIssueDateRangeCalendar";

interface IssueDateCalendarProps {
  /** Which of the two dates this calendar edits. */
  field: IssueDateField;
  /** The issue's start date — edited when `field` is "start", shown otherwise. */
  startDate: string | null | undefined;
  /** The issue's due date — edited when `field` is "due", shown otherwise. */
  dueDate: string | null | undefined;
  onChange: (date: string | null) => void;
  /** Label for the clear-to-no-date button. */
  clearLabel: string;
}

/**
 * IssueDateCalendar — a single-date calendar with a "no date" clear footer.
 * Container-agnostic: drop it inside a Popover (the issues-list row cell) or a
 * context-menu sub-content (the right-click menu) so both edit dates the same
 * way. Closing the host is the caller's concern.
 *
 * Serves both of an issue's dates. It edits the one `field` names and paints
 * the other with the span between them (useIssueDateRangeCalendar), so the
 * start and due editors stay one component rather than two that drift.
 * Neither date constrains the other here: an inverted pair is warned about,
 * never blocked (see issueDates.ts).
 */
export function IssueDateCalendar({
  field,
  startDate,
  dueDate,
  onChange,
  clearLabel,
}: IssueDateCalendarProps) {
  const rangeProps = useIssueDateRangeCalendar(field, startDate, dueDate);
  const value = field === "start" ? startDate : dueDate;
  return (
    <div>
      <Calendar
        {...rangeProps}
        mode="single"
        selected={value ? parseISO(value) : undefined}
        onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : null)}
        initialFocus
      />
      <div className="border-t p-1">
        <button
          type="button"
          onClick={() => onChange(null)}
          className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <CalendarOff className="h-3.5 w-3.5" />
          {clearLabel}
        </button>
      </div>
    </div>
  );
}
